import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import {
  gerarUnidades,
  lerUnidadesDaTorre,
  resumirGeracao,
  salvarAndares,
  salvarTorre,
  validarEstrutura,
  SELECT_TORRE,
  type Andar,
  type EstruturaDaTorre,
  type UnidadeDeTorre,
} from '@/lib/torresRepo';
import {
  interpretarErroDeLimite,
  invalidarUsoDoPlano,
  mensagemDeUpgrade,
} from '@/lib/planoLimites';

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300';

function TorreDetailPage() {
  const { id: condoId, torreId } = useParams<{ id: string; torreId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [condoNome, setCondoNome] = useState('');
  const [nome, setNome] = useState('');
  const [pavimentos, setPavimentos] = useState(0);
  const [aptosPorAndar, setAptosPorAndar] = useState(0);
  const [areaConstruida, setAreaConstruida] = useState(0);
  const [primeiroPavimento, setPrimeiroPavimento] = useState(1);
  const [pavimentosComUnidades, setPavimentosComUnidades] = useState(0);
  const [andares, setAndares] = useState<Andar[]>([]);
  const [unidades, setUnidades] = useState<UnidadeDeTorre[]>([]);
  const [geradasEm, setGeradasEm] = useState<string | null>(null);
  const [editAndar, setEditAndar] = useState<Andar | null>(null);
  const [deleteAndar, setDeleteAndar] = useState<number | null>(null);
  const [showAddAndar, setShowAddAndar] = useState(false);
  const [confirmandoGeracao, setConfirmandoGeracao] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const carregar = useCallback(async () => {
    if (!condoId || !torreId) return;
    setLoading(true);
    try {
      const [condoRes, torreRes] = await Promise.all([
        supabase.from('condominios').select('nome').eq('id', condoId).single(),
        supabase.from('torres').select(SELECT_TORRE).eq('id', torreId).single(),
      ]);

      if (condoRes.error || torreRes.error) {
        setError('Erro ao carregar dados.');
        return;
      }

      const t = torreRes.data;
      setCondoNome(condoRes.data?.nome || '');
      setNome(t?.nome || '');
      setPavimentos(t?.pavimentos || 0);
      setAptosPorAndar(t?.aptos_por_andar || 0);
      setAreaConstruida(t?.area_construida || 0);
      setPrimeiroPavimento(t?.primeiro_pavimento_com_unidades || 1);
      setPavimentosComUnidades(t?.pavimentos_com_unidades || 0);
      setAndares((t?.andares as Andar[]) || []);
      setGeradasEm(t?.unidades_geradas_em || null);

      // As unidades da torre vêm de `imoveis`, não de uma cópia no jsonb: são
      // as mesmas que a aba Imóveis lista, e editar por lá tem de aparecer aqui.
      setUnidades(await lerUnidadesDaTorre(torreId));
      setError(null);
    } catch {
      setError('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, [condoId, torreId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const estrutura: EstruturaDaTorre = {
    pavimentos,
    primeiroPavimento,
    pavimentosComUnidades,
    unidadesPorPavimento: aptosPorAndar,
  };
  const problemasDaEstrutura = validarEstrutura(estrutura);
  const resumo = resumirGeracao(estrutura);
  const podeGerar = !geradasEm && problemasDaEstrutura.length === 0 && !!resumo;

  const handleSaveTorre = async () => {
    if (!torreId) return;
    setSalvando(true);
    try {
      await salvarTorre(torreId, {
        nome,
        pavimentos,
        unidadesPorPavimento: aptosPorAndar,
        areaConstruida: areaConstruida || null,
        primeiroPavimento,
        pavimentosComUnidades: pavimentosComUnidades || null,
        andares,
      });
      showToast(`Torre "${nome}" salva com sucesso.`, 'success');
    } catch (e: any) {
      showToast(`Erro ao salvar: ${e.message}`, 'error');
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Grava a lista de andares no banco e só então atualiza a tela.
   *
   * Antes o andar entrava no `useState` e nada mais: aparecia na tabela,
   * sumia no F5 (P-34). Aqui a ordem é inversa — o banco primeiro, o estado
   * depois, para a tela nunca mostrar um andar que não existe.
   */
  const persistirAndares = async (proximos: Andar[], mensagem: string) => {
    if (!torreId) return;
    const anteriores = andares;
    setAndares(proximos);
    try {
      await salvarAndares(torreId, proximos);
      showToast(mensagem, 'success');
    } catch (e: any) {
      setAndares(anteriores);
      showToast(`Erro ao salvar andar: ${e.message}`, 'error');
    }
  };

  const handleDeleteAndar = async (andar: number) => {
    setDeleteAndar(null);
    await persistirAndares(
      andares.filter((a) => a.andar !== andar),
      `Andar ${andar} excluído com sucesso.`,
    );
  };

  const handleGerarUnidades = async () => {
    if (!torreId || !podeGerar) return;
    setSalvando(true);
    try {
      const gerado = await gerarUnidades(torreId, estrutura);
      invalidarUsoDoPlano();
      setConfirmandoGeracao(false);
      showToast(
        `${gerado.criadas} unidade(s) geradas: ${gerado.primeira} a ${gerado.ultima}.`,
        'success',
      );
      await carregar();
    } catch (e: any) {
      const limite = interpretarErroDeLimite(e);
      showToast(limite ? mensagemDeUpgrade(limite) : `Erro: ${e.message}`, 'error');
      setConfirmandoGeracao(false);
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl flex flex-col items-center gap-3 py-20">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-500"></i>
          </div>
          <p className="text-sm text-foreground-600">{error}</p>
          <Link
            to={`/condominios/${condoId}/torres`}
            className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap"
          >
            Voltar
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const unidadesAtivas = unidades.filter((u) => u.ativo !== false);

  return (
    <DashboardLayout>
      <div className="max-w-4xl">
        {/* breadcrumb */}
        <div className="mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/condominios"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>
              Condomínios
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            <Link
              to={`/condominios/${condoId}/torres`}
              className="text-sm text-foreground-600 hover:text-foreground-800 transition-colors"
            >
              {condoNome}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            <span className="text-sm font-semibold text-foreground-900">{nome}</span>
          </div>
        </div>

        <h1 className="text-xl font-bold text-foreground-900 mb-6">Configuração da Torre</h1>

        {/* características físicas */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">
            Características Físicas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="torre_nome"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Nome / Bloco
              </label>
              <input
                id="torre_nome"
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className={CLASSE_CAMPO}
              />
            </div>
            <div>
              <label
                htmlFor="torre_area"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Área Construída (m²)
              </label>
              <input
                id="torre_area"
                type="number"
                min={0}
                value={areaConstruida}
                onChange={(e) => setAreaConstruida(parseFloat(e.target.value) || 0)}
                className={CLASSE_CAMPO}
              />
            </div>
            <div>
              <label
                htmlFor="torre_pavimentos"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Total de pavimentos
              </label>
              <input
                id="torre_pavimentos"
                type="number"
                min={0}
                value={pavimentos}
                onChange={(e) => setPavimentos(parseInt(e.target.value) || 0)}
                className={CLASSE_CAMPO}
              />
            </div>
            <div>
              <label
                htmlFor="torre_pav_unidades"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Pavimentos com unidades
              </label>
              <input
                id="torre_pav_unidades"
                type="number"
                min={0}
                max={pavimentos}
                value={pavimentosComUnidades}
                onChange={(e) => setPavimentosComUnidades(parseInt(e.target.value) || 0)}
                className={CLASSE_CAMPO}
              />
            </div>
            <div>
              <label
                htmlFor="torre_primeiro_pav"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                A partir do pavimento
              </label>
              <input
                id="torre_primeiro_pav"
                type="number"
                min={1}
                value={primeiroPavimento}
                onChange={(e) => setPrimeiroPavimento(parseInt(e.target.value) || 1)}
                className={CLASSE_CAMPO}
              />
            </div>
            <div>
              <label
                htmlFor="torre_aptos"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Unidades por pavimento
              </label>
              <input
                id="torre_aptos"
                type="number"
                min={0}
                value={aptosPorAndar}
                onChange={(e) => setAptosPorAndar(parseInt(e.target.value) || 0)}
                className={CLASSE_CAMPO}
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 border-t border-background-200/70">
            <button
              onClick={() => void handleSaveTorre()}
              disabled={salvando}
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {salvando ? 'Salvando...' : 'Salvar Torre'}
            </button>
          </div>
        </div>

        {/* unidades da torre */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg mb-6">
          <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">
                Unidades da torre
                <span className="text-xs font-normal text-foreground-500 ml-2">
                  ({unidadesAtivas.length} unidade(s))
                </span>
              </h3>
              {geradasEm && (
                <p className="text-xs text-foreground-500 mt-0.5">
                  Geradas em {new Date(geradasEm).toLocaleDateString('pt-BR')} — a geração acontece
                  uma única vez por torre.
                </p>
              )}
            </div>
            {!geradasEm && (
              <button
                onClick={() => setConfirmandoGeracao(true)}
                disabled={!podeGerar || salvando}
                title={
                  podeGerar
                    ? undefined
                    : 'Informe pavimentos com unidades e unidades por pavimento, e salve a torre.'
                }
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-community-line"></i>
                Gerar unidades
              </button>
            )}
          </div>

          {unidades.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-home-4-line text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">Nenhuma unidade cadastrada nesta torre.</p>
              {!geradasEm && (
                <p className="text-xs text-foreground-400 mt-1">
                  Preencha a estrutura acima e use "Gerar unidades", ou cadastre uma a uma na aba
                  Imóveis.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Unidade
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Andar
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Tipo
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Proprietário
                    </th>
                    <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Situação
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {unidades.map((unidade) => (
                    <tr
                      key={unidade.id}
                      className={`hover:bg-background-100/30 transition-colors ${unidade.ativo === false ? 'opacity-55' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-foreground-800">
                          {unidade.unidade}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-700">{unidade.andar || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600">
                          {unidade.tipo || 'Apartamento'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600">
                          {unidade.proprietario || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {unidade.ativo === false ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                            Inativa
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            {unidade.status || 'Ativo'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* andares */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg">
          <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground-900">
              Configuração de Andares
              <span className="text-xs font-normal text-foreground-500 ml-2">
                ({andares.length} andar(es))
              </span>
            </h3>
            <button
              onClick={() => setShowAddAndar(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Adicionar Andar
            </button>
          </div>

          {andares.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-stack-line text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">
                Nenhum andar cadastrado. Clique em "Adicionar Andar" para começar.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Andar
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Unidades
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Descrição
                    </th>
                    <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {andares.map((andar) => (
                    <tr key={andar.andar} className="hover:bg-background-100/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-foreground-800">
                          {andar.andar}º
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-700">
                          {andar.unidades} unidade(s)
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600">
                          {andar.descricao || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditAndar(andar)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                            title="Editar"
                          >
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setDeleteAndar(andar.andar)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* modal adicionar/editar andar */}
      {(showAddAndar || editAndar) && (
        <AndarFormModal
          andar={editAndar}
          onSave={(data) => {
            const editando = editAndar;
            setShowAddAndar(false);
            setEditAndar(null);
            void persistirAndares(
              editando
                ? andares.map((a) => (a.andar === editando.andar ? data : a))
                : [...andares, data].sort((a, b) => a.andar - b.andar),
              editando
                ? `Andar ${data.andar} atualizado com sucesso.`
                : `Andar ${data.andar} adicionado com sucesso.`,
            );
          }}
          onClose={() => {
            setShowAddAndar(false);
            setEditAndar(null);
          }}
          andaresExistentes={andares.map((a) => a.andar)}
          editando={!!editAndar}
        />
      )}

      {/* modal excluir andar */}
      {deleteAndar !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteAndar(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir o <strong>{deleteAndar}º andar</strong>? As unidades já
              cadastradas continuam existindo — sai só a configuração do andar.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteAndar(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => void handleDeleteAndar(deleteAndar)}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* modal confirmar geração */}
      {confirmandoGeracao && resumo && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="titulo_confirmar_geracao"
        >
          <div
            className="absolute inset-0 bg-foreground-900/50"
            onClick={() => setConfirmandoGeracao(false)}
            aria-hidden="true"
          ></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-md shadow-lg">
            <div className="p-6 pb-4 flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-amber-600 text-xl"></i>
              </div>
              <div className="min-w-0">
                <h3
                  id="titulo_confirmar_geracao"
                  className="text-base font-semibold text-foreground-900"
                >
                  Revise os dados, pois as unidades só podem ser geradas uma única vez
                </h3>
                <p className="text-sm text-foreground-600 mt-1.5">
                  Serão criadas <strong>{resumo.total} unidade(s)</strong>, de{' '}
                  <strong>{resumo.primeira}</strong> a <strong>{resumo.ultima}</strong>. Depois
                  disso, a edição é uma a uma, na aba Imóveis.
                </p>
              </div>
            </div>

            <div className="px-6">
              <div className="flex flex-wrap gap-1.5">
                {resumo.amostra.map((unidade, indice) => (
                  <span key={unidade}>
                    {resumo.truncada && indice === resumo.amostra.length - 2 && (
                      <span className="text-foreground-400 text-xs mr-1.5">…</span>
                    )}
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-background-100 text-xs font-medium text-foreground-700">
                      {unidade}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 pt-5">
              <button
                onClick={() => setConfirmandoGeracao(false)}
                disabled={salvando}
                className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors disabled:opacity-60"
              >
                Revisar
              </button>
              <button
                onClick={() => void handleGerarUnidades()}
                disabled={salvando}
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {salvando ? 'Gerando...' : `Gerar ${resumo.total} unidade(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
              toast.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : toast.type === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-background-50 text-foreground-800 border border-background-200/70'
            }`}
          >
            <i
              className={`${
                toast.type === 'success'
                  ? 'ri-checkbox-circle-line text-green-600'
                  : toast.type === 'error'
                    ? 'ri-close-circle-line text-red-600'
                    : 'ri-information-line text-primary-600'
              }`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

/* ---------- MODAL ANDAR ---------- */
function AndarFormModal({
  andar,
  onSave,
  onClose,
  andaresExistentes,
  editando,
}: {
  andar: Andar | null;
  onSave: (data: Andar) => void;
  onClose: () => void;
  andaresExistentes: number[];
  editando: boolean;
}) {
  const [andarNum, setAndarNum] = useState(andar?.andar || andaresExistentes.length + 1);
  const [unidades, setUnidades] = useState(andar?.unidades || 4);
  const [descricao, setDescricao] = useState(andar?.descricao || '');
  const [erro, setErro] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editando && andaresExistentes.includes(andarNum)) {
      setErro(`O andar ${andarNum} já existe nesta torre.`);
      return;
    }
    if (andarNum < 1) {
      setErro('O número do andar deve ser maior que zero.');
      return;
    }
    onSave({ andar: andarNum, unidades, descricao });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-md shadow-lg">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground-900">
            {editando ? 'Editar Andar' : 'Adicionar Andar'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          <div>
            <label
              htmlFor="andar_numero"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Nº do Andar *
            </label>
            <input
              id="andar_numero"
              type="number"
              value={andarNum}
              onChange={(e) => {
                setAndarNum(parseInt(e.target.value) || 0);
                setErro('');
              }}
              disabled={editando}
              min={1}
              required
              className={`w-full px-3 py-2.5 rounded-md border text-sm ${
                editando
                  ? 'border-background-200/70 bg-background-100/50 text-foreground-500'
                  : 'border-background-300/60 bg-background-50'
              }`}
            />
            {erro && <p className="text-xs text-red-600 mt-1">{erro}</p>}
          </div>

          <div>
            <label
              htmlFor="andar_unidades"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Nº de Unidades
            </label>
            {/* Zero e um valor legitimo: terreo com salao de festas e cobertura
                com casa de maquinas sao pavimentos sem unidade. Com `min={1}` o
                formulario recusava em silencio — o navegador barra o submit e a
                tela nao diz por que. */}
            <input
              id="andar_unidades"
              type="number"
              value={unidades}
              onChange={(e) => setUnidades(parseInt(e.target.value) || 0)}
              min={0}
              className={CLASSE_CAMPO}
            />
            <p className="text-xs text-foreground-500 mt-1">
              Zero para pavimento sem unidades — garagem, salão de festas, casa de máquinas.
            </p>
          </div>

          <div>
            <label
              htmlFor="andar_descricao"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Descrição
            </label>
            <input
              id="andar_descricao"
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: Salão de festas, depósito..."
              className={CLASSE_CAMPO}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-background-200/70">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {editando ? 'Salvar' : 'Adicionar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TorreDetailPage;
